import React, { useState, useMemo, useEffect, ChangeEvent } from 'react'
import { Typography, Flex, Pagination, PaginationProps } from 'antd'
import {
  CardDescription,
  CardHeader,
  SectionCard,
} from '@/shared/ui/layout/styled'
import { Icon } from '@/shared/ui/Icon/Icon'
import {
  PartnerCard,
  PartnerId,
  PartnerName,
  PartnersGrid,
  PaginationContainer,
  PagerButton,
} from './styled'
import { SmallInput } from '@/shared/ui/components/Input'
import { useBreakpoint } from '@/app/hooks/useBreakpoint'

const { Title, Text } = Typography

export interface Partner {
  id: string
  name: string
  text?: string
}

export interface SubPartnersCardProps {
  partners: Partner[]
  selectedPartner?: string
  onPartnerSelect?: (partnerId: string) => void
}

const itemRender: PaginationProps['itemRender'] = (
  _,
  type,
  originalElement
) => {
  if (type === 'prev') {
    return (
      <PagerButton gap={4} align="center">
        <Icon name="pager_left" size={16} />
        Предыдущая
      </PagerButton>
    )
  }
  if (type === 'next') {
    return (
      <PagerButton gap={4} align="center">
        Следующая
        <Icon name="pager_right" size={16} />
      </PagerButton>
    )
  }
  return originalElement
}

export const SubPartnersCard: React.FC<SubPartnersCardProps> = ({
  partners,
  selectedPartner,
  onPartnerSelect,
}) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState('')
  const { isXs, isSm, isMd, isLg, isXl } = useBreakpoint()
  const isMobile = isXs || isSm

  const ITEMS_PER_PAGE = useMemo(() => {
    if (isXs || isSm) return 3
    if (isMd) return 6
    if (isLg) return 9
    if (isXl) return 12
    return 12
  }, [isXs, isSm, isMd, isLg, isXl])

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }

  const filteredPartners = useMemo(() => {
    if (!searchQuery.trim()) return partners

    return partners.filter(
      (partner) =>
        partner.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        partner.id.toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [partners, searchQuery])

  const handlePartnerClick = (partnerId: string) => {
    onPartnerSelect?.(partnerId)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const paginatedPartners = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
    const endIndex = startIndex + ITEMS_PER_PAGE
    return filteredPartners.slice(startIndex, endIndex)
  }, [filteredPartners, currentPage, ITEMS_PER_PAGE])

  const totalPages = Math.ceil(filteredPartners.length / ITEMS_PER_PAGE)

  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, filteredPartners.length])

  return (
    <SectionCard>
      <CardHeader gap={8}>
        <Icon name="subpartner_title" size={32} />
        <Title level={2}>Субпартнерские учетные записи Wialon</Title>
      </CardHeader>
      <Flex vertical gap={24}>
        <CardDescription>
          Выберите субпартнера, чтобы переключать контекст и загружать сущность
          именно из выбранной учетной записи.
        </CardDescription>

        <SmallInput
          label="Субпартнер"
          placeholder="Поиск по названию или ID"
          prefix={<Icon name="input_search" size={16} />}
          value={searchQuery}
          onChange={handleSearch}
          style={{ maxWidth: '436px' }}
        />

        {filteredPartners.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#8C8C8C',
            }}
          >
            <Icon name="empty_data" size={32} />
            <Text
              type="secondary"
              style={{
                display: 'block',
                marginTop: '8px',
                fontSize: '14px',
              }}
            >
              {searchQuery
                ? 'Субпартнерские учетные записи по вашему запросу не найдены'
                : 'Субпартнерские учетные записи не найдены'}
            </Text>
          </div>
        ) : (
          <>
            <PartnersGrid>
              {paginatedPartners.map((partner) => (
                <PartnerCard
                  vertical
                  justify="space-between"
                  key={partner.id}
                  onClick={() => handlePartnerClick(partner.id)}
                  $isSelected={selectedPartner === partner.id}
                >
                  <PartnerName>{partner.name}</PartnerName>
                  <Flex gap={8} align="center">
                    <Icon name="subpartner_item" size={16} />
                    <PartnerId>ID: {partner.id}</PartnerId>
                  </Flex>
                </PartnerCard>
              ))}
            </PartnersGrid>

            {totalPages > 1 && (
              <PaginationContainer justify={!isMobile ? 'end' : 'center'}>
                <Pagination
                  current={currentPage}
                  total={filteredPartners.length}
                  pageSize={ITEMS_PER_PAGE}
                  onChange={handlePageChange}
                  showSizeChanger={false}
                  itemRender={itemRender}
                />
              </PaginationContainer>
            )}
          </>
        )}
      </Flex>
    </SectionCard>
  )
}
